import { useCallback } from 'react'
import { IconButton, Tooltip } from 'shared/ui'
import { downloadFile } from 'shared/lib'
import {
  type AudioPlayerProps,
  type IHandleTrackOrganizationActivity,
} from 'shared/ui/AudioPlayer/ui/AudioPlayer'
import { ZIndex } from 'shared/constants/zIndex'

export const AudioPlayerActions = ({
  url,
  setShowTranscription,
  showTranscription,
  is_auto_transcribe,
  fileName,
  retryTranscript,
  canTranscript,
  disableTranscript,
  setScrollBottomTrigger,
  hideTranscriptionButton,
  transcriptStatus = null,
  isCallAudio,
  isHipaaCompliant,
  record,
  handleTrackOrganizationActivity,
  source,
}: AudioPlayerProps & {
  showTranscription: boolean
  hideTranscriptionButton?: boolean
  setShowTranscription: (value: boolean) => void
  setScrollBottomTrigger?: () => void
  isHipaaCompliant?: boolean
  handleTrackOrganizationActivity?: (data: IHandleTrackOrganizationActivity) => void
  source?: string
}) => {
  const toggleTranscription = () => {
    if (disableTranscript) return

    if (!is_auto_transcribe && transcriptStatus === null) {
      retryTranscript?.()
    }

    if (canTranscript || transcriptStatus || is_auto_transcribe) {
      setShowTranscription(!showTranscription)
    }

    setScrollBottomTrigger?.()
  }

  const isNeedPayForTranscript = transcriptStatus !== 'completed' && source !== 'ai_calling_agent'

  const tooltipLabel = useCallback(() => {
    if (showTranscription) {
      return 'Hide transcript'
    }
    return isNeedPayForTranscript ? 'Transcribe & summarize' : 'Show transcript'
  }, [isNeedPayForTranscript, showTranscription])

  const tooltipDesc = useCallback(() => {
    if (showTranscription) {
      return ''
    }
    return isNeedPayForTranscript ? '1 credit per minute for transcription' : ''
  }, [isNeedPayForTranscript, showTranscription])

  return (
    <>
      <Tooltip label={'Download'} placement={'top'}>
        <IconButton
          icon={'download'}
          fontSize={16}
          color={'secondary'}
          variant={'icon'}
          size={'small'}
          onClick={() => {
            url && downloadFile(url, fileName)
            if (isCallAudio && isHipaaCompliant && record?.record_id) {
              handleTrackOrganizationActivity?.({
                event: 'phi.call.recording.accessed',
                data: {
                  id: record?.id,
                  recording_id: record?.record_id,
                },
              })
            }
          }}
          ariaLabel='AudioPlayerDownload'
        />
      </Tooltip>
      {!hideTranscriptionButton && retryTranscript && (
        <Tooltip
          type={'description'}
          label={tooltipLabel()}
          desc={tooltipDesc()}
          placement={'top'}
          zIndex={ZIndex.OVERLAY_BASE_501}
        >
          <IconButton
            icon={
              showTranscription
                ? 'chevronUp'
                : isNeedPayForTranscript
                ? 'transcript2'
                : 'chevronDown'
            }
            loading={['in_progress', 'init_auto', 'init_manual', 'init_retry'].includes(
              `${transcriptStatus}`
            )}
            spinnerLoaderProps={{
              size: 14,
              determinatecolor: 'var(--gray-30)',
              indeterminatecolor: 'var(--gray-70)',
              ariaLabel: 'AudioPlayerActions',
            }}
            active={showTranscription}
            fontSize={16}
            disabled={disableTranscript}
            color={'secondary'}
            variant={'icon'}
            size={'small'}
            onClick={toggleTranscription}
            ariaLabel='AudioPlayerTranscription'
          />
        </Tooltip>
      )}
    </>
  )
}
